import Phaser from 'phaser';

import { SCREEN_SIZES } from '@/shared/config/ui';
import { InstructionInputController } from '@/features/laneChooserPhaser/game/objects/InstructionInputController';
import { LottieCanvasSprite } from '@/features/laneChooserPhaser/game/objects/LottieCanvasSprite';
import { supportsTouch } from '@/features/laneChooserPhaser/game/utils/input';
import {
	getInstructionAnimation,
	getInstructionTooltipKey,
} from '@/features/laneChooserPhaser/game/utils/instructionStages';

import type { LaneChooserInstructions } from '@/features/laneChooserPhaser/game/types';
import type { InstructionStage } from '@/features/laneChooserPhaser/game/utils/instructionStages';
import type { InstructionAnimation } from '@/features/laneChooserPhaser/instructionAnimations';

type InstructionControllerConfig = {
	scene: Phaser.Scene;
	container: Phaser.GameObjects.Container;
	viewportWidth: number;
	renderScaleX: number;
	renderScaleY: number;
	layout: LaneChooserInstructions;
	lottieSourceRoot: HTMLElement;
	showInstructions: boolean;
	onMoveLeft?: () => void;
	onMoveRight?: () => void;
	onJump?: () => void;
	onStartForwardBoost?: () => void;
	onStopForwardBoost?: () => void;
	onStartReverseBoost?: () => void;
	onStopReverseBoost?: () => void;
	onInstructionComplete: () => void;
	onTooltipChange: (textKey: string | null, point: { x: number; y: number } | null) => void;
};

const INSTRUCTION_TOOLTIP_TOP_INSET_PX = 8;
const INSTRUCTION_STAGE_FADE_DURATION_MS = 200;

export class InstructionController {
	// Scene reference used for input events and animation registration.
	private readonly scene: Phaser.Scene;
	private readonly visualContainer: Phaser.GameObjects.Container;
	private readonly lottieSourceRoot: HTMLElement;
	// Whether touch/swipe instructions should be shown instead of keyboard instructions.
	private readonly useSwipeInstructions: boolean;
	// Original CSS viewport width passed down from React.
	private readonly viewportWidth: number;
	// X-axis multiplier from CSS pixels into Phaser render pixels.
	private readonly renderScaleX: number;
	// Y-axis multiplier from CSS pixels into Phaser render pixels.
	private readonly renderScaleY: number;
	// Theme-owned instruction placement and size settings.
	private readonly layout: LaneChooserInstructions;
	private readonly onInstructionComplete: () => void;
	private readonly onTooltipChange: InstructionControllerConfig['onTooltipChange'];
	private readonly inputController: InstructionInputController;
	private activeAnimation: LottieCanvasSprite | null = null;
	private displayWidth = 1;
	private displayHeight = 1;
	// Current staged hint shown to the player.
	private stage: InstructionStage = 'leftRight';
	// Fade tween currently animating the hint sprite between stages.
	private activeTransitionTween: Phaser.Tweens.Tween | null = null;
	// Blocks additional stage changes until the current fade sequence is complete.
	private isTransitioning = false;

	constructor({
		scene,
		container,
		viewportWidth,
		renderScaleX,
		renderScaleY,
		layout,
		lottieSourceRoot,
		showInstructions,
		onMoveLeft,
		onMoveRight,
		onJump,
		onStartForwardBoost,
		onStopForwardBoost,
		onStartReverseBoost,
		onStopReverseBoost,
		onInstructionComplete,
		onTooltipChange,
	}: InstructionControllerConfig) {
		this.scene = scene;
		this.visualContainer = this.scene.add.container(0, 0);
		container.add(this.visualContainer);
		this.lottieSourceRoot = lottieSourceRoot;
		this.viewportWidth = viewportWidth;
		this.renderScaleX = renderScaleX;
		this.renderScaleY = renderScaleY;
		this.layout = layout;
		this.onInstructionComplete = onInstructionComplete;
		this.onTooltipChange = onTooltipChange;
		this.useSwipeInstructions = viewportWidth < SCREEN_SIZES.TABLET || supportsTouch();
		this.stage = showInstructions ? 'leftRight' : 'done';

		this.syncStage();
		this.inputController = new InstructionInputController({
			scene: this.scene,
			renderScaleX: this.renderScaleX,
			renderScaleY: this.renderScaleY,
			getStage: () => this.stage,
			onStageChange: (stage) => this.setStage(stage),
			onMoveLeft,
			onMoveRight,
			onJump,
			onStartForwardBoost,
			onStopForwardBoost,
			onStartReverseBoost,
			onStopReverseBoost,
		});
	}

	update() {
		this.activeAnimation?.update();
	}

	complete() {
		this.inputController.cancelActiveGesture();
		this.setStage('done', true);
	}

	destroy() {
		this.inputController.destroy();
		this.stopTransitionTween();
		this.clearTooltip();
		this.activeAnimation?.destroy();
		this.activeAnimation = null;
		this.visualContainer.destroy();
	}

	private setStage(nextStage: InstructionStage, force = false) {
		if (this.stage === nextStage) {
			return;
		}

		if (this.isTransitioning) {
			if (!force) {
				return;
			}
			this.stopTransitionTween();
			this.visualContainer.setAlpha(1);
		}

		const currentAnimation = getInstructionAnimation(this.stage, this.useSwipeInstructions);
		const currentTooltipTextKey = getInstructionTooltipKey(this.stage, this.useSwipeInstructions);
		const nextAnimation = getInstructionAnimation(nextStage, this.useSwipeInstructions);
		const nextTooltipTextKey = getInstructionTooltipKey(nextStage, this.useSwipeInstructions);

		if (!this.activeAnimation || !currentAnimation || !currentTooltipTextKey) {
			this.stage = nextStage;
			this.syncStage();
			if (nextStage === 'done') {
				this.onInstructionComplete();
			}
			return;
		}

		this.stopTransitionTween();
		this.isTransitioning = true;
		this.activeTransitionTween = this.scene.tweens.add({
			targets: this.visualContainer,
			alpha: 0,
			duration: INSTRUCTION_STAGE_FADE_DURATION_MS,
			ease: 'Power1',
			onComplete: () => {
				this.activeTransitionTween = null;
				this.stage = nextStage;

				if (!nextAnimation || !nextTooltipTextKey) {
					this.destroyActiveAnimation();
					this.visualContainer.setVisible(false).setAlpha(1);
					this.clearTooltip();
					if (nextStage === 'done') {
						this.onInstructionComplete();
					}
					this.isTransitioning = false;
					return;
				}

				this.applyStageContent(nextAnimation, nextTooltipTextKey);
				this.visualContainer.setAlpha(0);
				this.activeTransitionTween = this.scene.tweens.add({
					targets: this.visualContainer,
					alpha: 1,
					duration: INSTRUCTION_STAGE_FADE_DURATION_MS,
					ease: 'Power1',
					onComplete: () => {
						this.activeTransitionTween = null;
						this.isTransitioning = false;
					},
				});
			},
		});
	}

	private syncStage() {
		const animation = getInstructionAnimation(this.stage, this.useSwipeInstructions);
		const tooltipTextKey = getInstructionTooltipKey(this.stage, this.useSwipeInstructions);

		if (!animation || !tooltipTextKey) {
			this.destroyActiveAnimation();
			this.visualContainer.setVisible(false).setAlpha(1);
			this.clearTooltip();
			return;
		}

		this.visualContainer.setAlpha(1);
		this.applyStageContent(animation, tooltipTextKey);
	}

	private applyStageContent(animation: InstructionAnimation, tooltipTextKey: string) {
		this.destroyActiveAnimation();
		this.activeAnimation = new LottieCanvasSprite({
			scene: this.scene,
			parent: this.visualContainer,
			sourceRoot: this.lottieSourceRoot,
			textureKey: animation.textureKey,
			animationData: animation.animationData,
			canvasWidth: animation.canvasWidth,
			canvasHeight: animation.canvasHeight,
			loop: true,
			originX: 0,
			originY: 1,
		});
		this.syncLayout(animation);
		this.visualContainer.setVisible(true);
		this.activeAnimation.setVisible(true);
		this.activeAnimation.playFromStart();
		this.syncTooltip(tooltipTextKey);
	}

	private syncLayout(animation: InstructionAnimation) {
		this.displayWidth =
			Phaser.Math.Clamp(
				(this.viewportWidth * this.layout.widthVw) / 100,
				this.layout.minWidthPx,
				this.layout.maxWidthPx,
			) * this.renderScaleX;
		this.displayHeight = (this.displayWidth * animation.canvasHeight) / animation.canvasWidth;

		this.visualContainer.setPosition(
			Math.round(this.layout.leftPx * this.renderScaleX),
			Math.ceil(this.scene.scale.height - this.layout.bottomPx * this.renderScaleY),
		);
		this.activeAnimation?.setDisplaySize(Math.round(this.displayWidth), Math.round(this.displayHeight));
	}

	private syncTooltip(tooltipTextKey: string) {
		const displayWidthCss = this.renderScaleX > 0 ? this.displayWidth / this.renderScaleX : this.displayWidth;
		const displayHeightCss = this.renderScaleY > 0 ? this.displayHeight / this.renderScaleY : this.displayHeight;
		const leftCss = this.renderScaleX > 0 ? this.visualContainer.x / this.renderScaleX : this.visualContainer.x;
		const bottomCss = this.renderScaleY > 0 ? this.visualContainer.y / this.renderScaleY : this.visualContainer.y;

		this.onTooltipChange(tooltipTextKey, {
			x: Math.round(leftCss + displayWidthCss / 2),
			y: Math.round(bottomCss - displayHeightCss + INSTRUCTION_TOOLTIP_TOP_INSET_PX),
		});
	}

	private destroyActiveAnimation() {
		this.activeAnimation?.destroy();
		this.activeAnimation = null;
	}

	// Clears the React tooltip whenever the instruction HUD is hidden or destroyed.
	private clearTooltip() {
		this.onTooltipChange(null, null);
	}

	// Stops the current fade tween so destroy and immediate state changes do not leave dangling callbacks behind.
	private stopTransitionTween() {
		this.activeTransitionTween?.stop();
		this.activeTransitionTween = null;
		this.isTransitioning = false;
	}
}
